import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminLogin = ({ setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  
  // 2FA State
  const [show2FAInput, setShow2FAInput] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [twoFAEmail, setTwoFAEmail] = useState('');

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!show2FAInput && !recaptchaToken) {
      toast.error(t('superadmin.login.recaptcha_required'));
      return;
    }
    
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/superadmin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          password, 
          recaptcha_token: recaptchaToken,
          mfa_code: show2FAInput ? mfaCode : undefined
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || t('auth.login_failed'));
      }

      // Check if 2FA is required
      if (data.requires_2fa) {
        setShow2FAInput(true);
        setTwoFAEmail(data.email);
        toast.info('A verification code has been sent to your email.');
        setLoading(false);
        return;
      }

      // Store token and user info
      localStorage.setItem('movemate_token', data.token);
      localStorage.setItem('movemate_user', JSON.stringify(data.user));
      
      setUser(data.user);
      
      toast.success(t('superadmin.login.welcome'));
      navigate('/superadmin');
    } catch (error) {
      toast.error(error.message || t('auth.login_failed'));
      
      // Check if 2FA code was wrong - don't reset the 2FA form
      const errorMsg = error.message?.toLowerCase() || '';
      if (errorMsg.includes('2fa') || errorMsg.includes('verification code')) {
        setMfaCode('');
      } else {
        setShow2FAInput(false);
        setMfaCode('');
        // Reset reCAPTCHA on error
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-slate-800 opacity-50"></div>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Back link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          {t('superadmin.login.back_to_movemate')}
        </Link>

        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-amber-500/20">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white">{t('superadmin.login.title')}</CardTitle>
            <CardDescription className="text-slate-400">
              {t('superadmin.login.description')}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              {/* 2FA Code Input */}
              {show2FAInput ? (
                <div className="space-y-4">
                  <Alert className="bg-blue-900/50 border-blue-700">
                    <Shield className="h-4 w-4 text-blue-400" />
                    <AlertDescription className="text-blue-200">
                      <p className="font-medium mb-1">Two-Factor Authentication</p>
                      <p className="text-sm">A 6-digit code has been sent to <strong>{twoFAEmail}</strong></p>
                    </AlertDescription>
                  </Alert>
                  
                  <div>
                    <Label className="text-slate-300">Verification Code</Label>
                    <Input
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 text-center tracking-widest text-xl"
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="superadmin-mfa-code"
                    />
                  </div>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShow2FAInput(false);
                      setMfaCode('');
                      recaptchaRef.current?.reset();
                      setRecaptchaToken(null);
                    }}
                    className="w-full text-slate-400 hover:text-white"
                  >
                    ← Back to login
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-slate-300">{t('superadmin.login.email_label')}</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('superadmin.login.email_placeholder')}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500"
                      required
                      data-testid="superadmin-email"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-300">{t('superadmin.login.password_label')}</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('superadmin.login.password_placeholder')}
                        className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-amber-500 pr-10"
                        required
                        data-testid="superadmin-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* reCAPTCHA */}
                  <div className="flex justify-center" data-testid="superadmin-recaptcha">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={handleRecaptchaExpired}
                      theme="dark"
                    />
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold"
                disabled={loading || (!show2FAInput && !recaptchaToken) || (show2FAInput && mfaCode.length !== 6)}
                data-testid="superadmin-login-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    {t('superadmin.login.authenticating')}
                  </span>
                ) : (
                  show2FAInput ? 'Verify Code' : t('superadmin.login.access_dashboard')
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-xs text-slate-500 text-center">
                {t('superadmin.login.authorized_only')}
                <br />
                {t('superadmin.login.access_logged')}
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          &copy; {new Date().getFullYear()} {t('superadmin.login.copyright')}
        </p>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
